// Zentrale Paketdefinition - 3 feste Pakete (S/M/L)
// Version 5.0 - XL entfernt, L übernimmt alle XL-Features

export interface PackageDefinition {
  id: string;
  name: string;
  shortName: string;
  category: 'S' | 'M' | 'L';
  categoryName: string;
  price: number;
  priceDisplay: string;
  features: string[];
  description: string;
  popular?: boolean;
  disabled?: boolean;
  statusLabel?: string;
}

export interface PackageLimits {
  maxPages: number;
  maxArticles: number;
}

export interface ValidationResult {
  valid: boolean;
  requiredPackage?: PackageDefinition;
  currentPackage?: PackageDefinition;
  message?: string;
}

// Die 3 Hauptpakete
export const ALL_PACKAGES: PackageDefinition[] = [
  {
    id: 's',
    name: 'Einsteiger',
    shortName: 'S',
    category: 'S',
    categoryName: 'Einsteiger',
    price: 49,
    priceDisplay: '49€',
    features: [
      'Detaillierter Analysebericht',
      'Handlungsempfehlungen',
      'Sofortiger Download'
    ],
    description: 'Für Selbstumsetzer mit hoher Eigenleistung'
  },
  {
    id: 'm',
    name: 'Standard',
    shortName: 'M',
    category: 'M',
    categoryName: 'Standard',
    price: 89,
    priceDisplay: '89€',
    features: [
      'Analysebericht (PDF)',
      'Handlungsempfehlungen (PDF)',
      'Allergen-/Zusatzstoffliste (Excel)'
    ],
    description: 'Teilautomatisierte Lösung'
  },
  {
    id: 'l',
    name: 'Professional',
    shortName: 'L',
    category: 'L',
    categoryName: 'Professional',
    price: 149,
    priceDisplay: '149€',
    features: [
      'Analysebericht (PDF)',
      'Handlungsempfehlungen (PDF)',
      'Allergen-/Zusatzstoffliste (Excel)',
      'Überarbeitete Karte mit Hinweistext'
    ],
    description: 'Premium-Lösung mit vollständiger Kennzeichnung',
    popular: true
  }
];

// Pakete nach Kategorie gruppiert
export const PACKAGES_BY_CATEGORY = {
  S: ALL_PACKAGES.filter(p => p.category === 'S'),
  M: ALL_PACKAGES.filter(p => p.category === 'M'),
  L: ALL_PACKAGES.filter(p => p.category === 'L')
};

// Kategorie-Beschreibungen mit Farben (Bronze, Silber, Gold)
export const CATEGORY_DESCRIPTIONS: Record<'S' | 'M' | 'L', { name: string; subtitle: string; color: string; textColor: string }> = {
  S: { name: 'Einsteiger', subtitle: 'Für Selbstumsetzer', color: 'bg-[#CD7F32]', textColor: 'text-gray-800' },
  M: { name: 'Standard', subtitle: 'Teilautomatisiert', color: 'bg-[#C0C0C0]', textColor: 'text-gray-800' },
  L: { name: 'Professional', subtitle: 'Vollständige Kennzeichnung', color: 'bg-[#FFD700]', textColor: 'text-gray-800' }
};

// Was Sie bekommen / Was Sie selbst tun - pro Kategorie
type YouDoItem = string | { main: string; sub: string[] };

export const CATEGORY_DELIVERABLES: Record<'S' | 'M' | 'L', {
  youGet: string[];
  youDo: YouDoItem[];
  effortLabel: string;
}> = {
  S: {
    youGet: ['Analysebericht (alle detaillierten Informationen)', 'Handlungsempfehlungen'],
    youDo: [{ 
      main: 'Karte selbst anpassen', 
      sub: ['Auf rechtliche Hinweise prüfen und ggf. anpassen', 'Hinweistext selbst einfügen'] 
    }, {
      main: 'Schriftliche Aufzeichnungen selbst erstellen',
      sub: ['Allergene und Zusatzstoffe je Produkt ermitteln', 'In eigener Liste dokumentieren (z. B. Excel)']
    },
    'Alles neu ausdrucken'],
    effortLabel: 'Hoher Aufwand'
  },
  M: {
    youGet: ['Analysebericht (alle detaillierten Informationen)', 'Handlungsempfehlungen', 'Allergen-/Zusatzstoffliste (schriftliche Aufzeichnungen)'],
    youDo: [{
      main: 'Karte selbst anpassen',
      sub: ['Auf rechtliche Hinweise prüfen und ggf. anpassen', 'Hinweistext selbst in die eigene Karte einfügen']
    },
    'Allergen-/Zusatzstoffliste sowie Karte ausdrucken'],
    effortLabel: 'Moderater Aufwand'
  },
  L: {
    youGet: ['Analysebericht (alle detaillierten Informationen)', 'Handlungsempfehlungen', 'Allergen-/Zusatzstoffliste (schriftliche Aufzeichnungen)', 'Überarbeitete Karte mit Hinweistext (inkl. Editor)'],
    youDo: [{
      main: '',
      sub: ['Nur auf rechtliche Hinweise prüfen und ggf. anpassen']
    },
    'Allergen-/Zusatzstoffliste sowie Karte ausdrucken'],
    effortLabel: 'Minimaler Aufwand'
  }
};

// Legacy-Mapping: Alte Paket-IDs zu neuen IDs (XL → L)
export const LEGACY_PACKAGE_MAPPING: Record<string, string> = {
  // Alte Klein/Groß IDs
  'sk': 's',
  'sg': 's',
  'mk': 'm',
  'mg': 'm',
  'lk': 'l',
  'lg': 'l',
  'xlk': 'l',
  'xlg': 'l',
  // Alte 1/2/3 Stufen
  's1': 's',
  's2': 's',
  's3': 's',
  'm1': 'm',
  'm2': 'm',
  'm3': 'm',
  'l1': 'l',
  'l2': 'l',
  'l3': 'l',
  'xl1': 'l',
  'xl2': 'l',
  'xl3': 'l',
  // Ganz alte String-IDs
  'starter': 's',
  'basic': 'm',
  'professional': 'l',
  'professional-plus': 'l',
  // XL → L Legacy-Mapping
  'xl': 'l'
};

/**
 * Holt ein Paket nach ID (unterstützt auch Legacy-IDs)
 */
export function getPackageById(id: string): PackageDefinition | undefined {
  if (!id) return undefined;
  
  const normalizedId = id.toLowerCase();
  
  // Direkte Suche
  let pkg = ALL_PACKAGES.find(p => p.id.toLowerCase() === normalizedId);
  if (pkg) return pkg;
  
  // Legacy-Mapping
  const mappedId = LEGACY_PACKAGE_MAPPING[normalizedId];
  if (mappedId) {
    pkg = ALL_PACKAGES.find(p => p.id.toLowerCase() === mappedId.toLowerCase());
    if (pkg) return pkg;
  }
  
  return undefined;
}

/**
 * Konvertiert alte Paket-IDs zu neuen IDs
 */
export function convertLegacyPackageId(id: string): string {
  if (!id) return id;
  const normalizedId = id.toLowerCase();
  return LEGACY_PACKAGE_MAPPING[normalizedId] || id;
}

/**
 * Holt den Anzeigenamen für ein Paket (auch für Legacy-IDs)
 */
export function getPackageDisplayName(id: string): string {
  const pkg = getPackageById(id);
  return pkg?.name || id;
}

/**
 * Alle verfügbaren (nicht deaktivierten) Pakete
 */
export function getAvailablePackages(): PackageDefinition[] {
  return ALL_PACKAGES.filter(pkg => !pkg.disabled);
}

/**
 * Pakete nach Kategorie filtern
 */
export function getPackagesByCategory(category: 'S' | 'M' | 'L'): PackageDefinition[] {
  return ALL_PACKAGES.filter(pkg => pkg.category === category);
}
